"use client";

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Database, Eye, Folder, HardDrive, KeyRound, MonitorUp, RefreshCw, Search, Send, ShieldCheck, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const SHARED_COORDINATOR_URL = (import.meta.env.VITE_SHARED_COORDINATOR_URL || 'https://next-events-engine.will-026.workers.dev').replace(/\/*$/, '');

type AssetAvailability = {
  primary: string;
  counts: Record<string, number>;
  syncStatuses?: Record<string, number>;
};

type AssetMetadata = {
  localFileId?: string;
  extension?: string;
  sizeBytes?: number;
  firstSeenAt?: string | number;
  lastVerifiedAt?: string | number;
  modifiedAt?: string | number;
  scope?: string;
  hostKind?: string;
  hostOnline?: number;
  driveLabel?: string;
  variantCount?: number;
  mp4VariantCount?: number;
  dxvVariantCount?: number;
  hasThumbnail?: boolean;
  hasScrubStrip?: boolean;
  hasProxy?: boolean;
  hasMp4Variant?: boolean;
  hasDxvVariant?: boolean;
  loopHamming?: number;
  beatCount?: number;
  creatorNames?: string[];
  projectNames?: string[];
  setNames?: string[];
  artistNames?: string[];
  resolumeDeckNames?: string[];
  resolumeClipColor?: string;
  localEventId?: string;
  layerCount?: number;
  attachedAssetCount?: number;
  attachedEntityCount?: number;
  generationRunCount?: number;
  exportCount?: number;
  updatedAt?: string | number;
};

type BrowserAccess = {
  mode: string;
  reachable: boolean;
  deviceId?: string;
  deviceName?: string;
  availability?: string;
  syncStatus?: string;
  pathDisplayName?: string;
  candidateCount?: number;
  canServeLocalFiles?: boolean;
  hasRelay?: boolean;
  hasTailscale?: boolean;
  hasLocalApi?: boolean;
  lastSeenAt?: number;
  freshnessWindowMs?: number;
  note?: string;
};

type AssetDerivatives = {
  hasThumbnail?: boolean;
  hasProxy?: boolean;
  thumbnail?: {
    id?: string;
    fileName?: string;
    mimeType?: string;
    extension?: string;
    updatedAt?: number;
    cachedOnDevice?: boolean;
    browserPreviewRequiresOnlineDevice?: boolean;
    browserPosterAvailable?: boolean;
    browserPosterDataUrl?: string;
  };
  proxy?: {
    id?: string;
    fileName?: string;
    mimeType?: string;
    extension?: string;
    updatedAt?: number;
    cachedOnDevice?: boolean;
    browserPreviewRequiresOnlineDevice?: boolean;
  };
};

type BrowserSummary = {
  organization?: { name: string; slug?: string };
  cloudRequired?: boolean;
  originalMediaUploadRequired?: boolean;
  assetPage?: {
    limit: number;
    offset: number;
    returned: number;
    total: number;
    hasMore: boolean;
  };
  browserMode?: {
    readOnly: boolean;
    canRequestPreview?: boolean;
    canRequestChanges?: boolean;
    reachableDeviceCount?: number;
    reason?: string;
  };
  counts?: {
    devices: number;
    libraries: number;
    assets: number;
    assetLocations: number;
    crates?: number;
    crateAssets?: number;
  };
  availability?: Record<string, number>;
  devices?: Array<{
    id?: string;
    deviceId?: string;
    name: string;
    clientType: string;
    appProduct: string;
    status?: string;
    browserAccess?: BrowserAccess;
    localCatalogUuid?: string;
    localHostUuid?: string;
    lastSeenAt?: number;
  }>;
  libraries?: Array<{
    id?: string;
    libraryId?: string;
    name: string;
    kind?: string;
    visibility?: string;
    localLibraryUuid?: string;
    folderCount?: number;
    membershipCount?: number;
  }>;
  assets?: Array<{
    id?: string;
    assetId?: string;
    title: string;
    mediaType: string;
    libraryId?: string;
    sourceApp?: string;
    tags?: string[];
    metadata?: AssetMetadata;
    availability?: AssetAvailability;
    durationMs?: number;
    width?: number;
    height?: number;
    codec?: string;
    container?: string;
    fileCount?: number;
    locationCount?: number;
    browserAccess?: BrowserAccess;
    derivatives?: AssetDerivatives;
  }>;
  crates?: Array<{
    id?: string;
    crateId?: string;
    name: string;
    kind?: string;
    status?: string;
    sourceKind?: "vj_loadout" | "vj_collection" | "event_tools_project" | "manual" | string;
    localCrateId?: string;
    eventId?: string;
    defaultSyncMode?: string;
    assetIds?: string[];
    assetCount?: number;
  }>;
};

const BROWSER_ASSET_PAGE_SIZE = 250;

type CoordinatorConfig = {
  protocolVersion: string;
  cloudRequired: boolean;
  originalMediaUploadRequired: boolean;
  clientApiBackend?: string;
  pairing?: {
    browserHandoff: boolean;
    qrCode: boolean;
    manualCode: boolean;
    localOnlyAllowed: boolean;
  };
};

type ApiResponse = {
  ok: boolean;
  data?: BrowserSummary;
  error?: string;
};

type ConfigResponse = {
  ok: boolean;
  data?: CoordinatorConfig;
  error?: string;
};

type JobResponse = {
  ok: boolean;
  data?: {
    syncJobId?: string;
    organizationId?: string;
  };
  error?: string;
};

export function LibraryBrowser() {
  const [summary, setSummary] = useState<BrowserSummary | null>(null);
  const [source, setSource] = useState<"device" | "cloud" | "local_mock" | null>(null);
  const [config, setConfig] = useState<CoordinatorConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [libraryId, setLibraryId] = useState<string>("all");
  const [crateId, setCrateId] = useState<string>("all");
  const [sourceApp, setSourceApp] = useState<string>("all");
  const [availability, setAvailability] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"title" | "source" | "availability" | "type" | "locations">("title");
  const [deviceId, setDeviceId] = useState(() => readStoredBrowserCredential("deviceId"));
  const [deviceToken, setDeviceToken] = useState(() => readStoredBrowserCredential("deviceToken"));
  const [localBrowserAccessUrl, setLocalBrowserAccessUrl] = useState(() => readStoredBrowserCredential("localBrowserAccessUrl"));
  const [localBrowserAccessToken, setLocalBrowserAccessToken] = useState(() => readStoredBrowserCredential("localBrowserAccessToken"));
  const [credentialText, setCredentialText] = useState("");
  const [requestingAssetId, setRequestingAssetId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const refresh = useCallback(async (credentialOverride?: { deviceId: string; deviceToken: string }) => {
    setLoading(true);
    setError(null);
    try {
    const configResponse = await fetch(`${coordinatorApiUrl("/api/client/config")}`, { cache: "no-store" });
      const configJson = (await configResponse.json().catch(() => null)) as ConfigResponse | null;
      if (configResponse.ok && configJson?.ok && configJson.data) setConfig(configJson.data);

      const trimmedDeviceId = (credentialOverride?.deviceId ?? deviceId).trim();
      const trimmedDeviceToken = (credentialOverride?.deviceToken ?? deviceToken).trim();
      if (trimmedDeviceId && trimmedDeviceToken) {
        const deviceSummaryResponse = await fetch(summaryPageUrl("/api/client/summary", 0), {
          cache: "no-store",
          headers: deviceCredentialHeaders(trimmedDeviceId, trimmedDeviceToken),
        });
        const deviceSummaryJson = (await deviceSummaryResponse.json().catch(() => null)) as ApiResponse | null;
        if (deviceSummaryResponse.ok && deviceSummaryJson?.ok && deviceSummaryJson.data) {
          window.localStorage.setItem("vjtools.browserLibrary.deviceId", trimmedDeviceId);
          window.localStorage.setItem("vjtools.browserLibrary.deviceToken", trimmedDeviceToken);
          setSummary(deviceSummaryJson.data);
          setSource("device");
          return;
        }
        setError(deviceSummaryJson?.error ?? "Stored device credential could not load the browser library.");
      }

      const cloudResponse = await fetch(summaryPageUrl("/api/dashboard/summary", 0), { cache: "no-store" });
      const cloudJson = (await cloudResponse.json().catch(() => null)) as ApiResponse | null;
      if (cloudResponse.ok && cloudJson?.ok && cloudJson.data) {
        setSummary(cloudJson.data);
        setSource("cloud");
        return;
      }

      const mockResponse = await fetch(summaryPageUrl("/api/client/mock/summary", 0), { cache: "no-store" });
      const mockJson = (await mockResponse.json().catch(() => null)) as ApiResponse | null;
      if (mockResponse.ok && mockJson?.ok && mockJson.data) {
        setSummary(mockJson.data);
        setSource("local_mock");
        setError(cloudJson?.error ?? null);
        return;
      }

      throw new Error(cloudJson?.error ?? mockJson?.error ?? "Could not load library metadata");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [deviceId, deviceToken]);

  const loadMoreAssets = useCallback(async () => {
    if (!summary?.assetPage?.hasMore || loadingMore) return;
    const offset = (summary.assets ?? []).length;
    setLoadingMore(true);
    setError(null);
    try {
      let response: Response;
      if (source === "device") {
        const trimmedDeviceId = deviceId.trim();
        const trimmedDeviceToken = deviceToken.trim();
        if (!trimmedDeviceId || !trimmedDeviceToken) throw new Error("Pair a trusted device credential before loading more asset metadata.");
        response = await fetch(summaryPageUrl("/api/client/summary", offset), {
          cache: "no-store",
          headers: deviceCredentialHeaders(trimmedDeviceId, trimmedDeviceToken),
        });
      } else if (source === "cloud") {
        response = await fetch(summaryPageUrl("/api/dashboard/summary", offset), { cache: "no-store" });
      } else {
        response = await fetch(summaryPageUrl("/api/client/mock/summary", offset), { cache: "no-store" });
      }
      const json = (await response.json().catch(() => null)) as ApiResponse | null;
      if (!response.ok || !json?.ok || !json.data) throw new Error(json?.error ?? `Coordinator returned HTTP ${response.status}`);
      setSummary((current) => {
        if (!current) return json.data ?? null;
        return {
          ...json.data,
          assets: [...(current.assets ?? []), ...(json.data?.assets ?? [])],
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load more synced asset metadata.");
    } finally {
      setLoadingMore(false);
    }
  }, [deviceId, deviceToken, loadingMore, source, summary]);

  const applyBrowserCredential = useCallback((credential: { deviceId: string; deviceToken: string; localBrowserAccessUrl?: string; localBrowserAccessToken?: string }) => {
    const nextDeviceId = credential.deviceId.trim();
    const nextDeviceToken = credential.deviceToken.trim();
    if (!nextDeviceId || !nextDeviceToken) {
      throw new Error("Credential JSON must include deviceId and deviceToken.");
    }
    setDeviceId(nextDeviceId);
    setDeviceToken(nextDeviceToken);
    const nextLocalBrowserAccessUrl = normalizeLocalBrowserAccessUrl(credential.localBrowserAccessUrl);
    const nextLocalBrowserAccessToken = normalizeLocalBrowserAccessToken(credential.localBrowserAccessToken);
    setLocalBrowserAccessUrl(nextLocalBrowserAccessUrl);
    setLocalBrowserAccessToken(nextLocalBrowserAccessToken);
    window.localStorage.setItem("vjtools.browserLibrary.deviceId", nextDeviceId);
    window.localStorage.setItem("vjtools.browserLibrary.deviceToken", nextDeviceToken);
    if (nextLocalBrowserAccessUrl) {
      window.localStorage.setItem("vjtools.browserLibrary.localBrowserAccessUrl", nextLocalBrowserAccessUrl);
    } else {
      window.localStorage.removeItem("vjtools.browserLibrary.localBrowserAccessUrl");
    }
    if (nextLocalBrowserAccessToken) {
      window.localStorage.setItem("vjtools.browserLibrary.localBrowserAccessToken", nextLocalBrowserAccessToken);
    } else {
      window.localStorage.removeItem("vjtools.browserLibrary.localBrowserAccessToken");
    }
    setCredentialText("");
    setError(null);
    void refresh({ deviceId: nextDeviceId, deviceToken: nextDeviceToken });
  }, [refresh]);

  useEffect(() => {
    const credential = readHashBrowserCredential();
    if (!credential) return;
    void Promise.resolve().then(() => {
      try {
        applyBrowserCredential(credential);
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not read credential from the browser handoff link.");
      }
    });
  }, [applyBrowserCredential]);

  useEffect(() => {
    void Promise.resolve().then(() => refresh());
  }, [refresh]);

  const libraries = summary?.libraries ?? [];
  const devices = summary?.devices ?? [];
  const crates = summary?.crates ?? [];
  const selectedCrate = useMemo(
    () => crates.find((crate) => crateIdentity(crate) === crateId) ?? null,
    [crateId, crates],
  );
  const selectedCrateAssetIds = useMemo(
    () => new Set((selectedCrate?.assetIds ?? []).filter(Boolean)),
    [selectedCrate],
  );
  const sourceOptions = useMemo(() => uniqueOptions((summary?.assets ?? []).map((asset) => asset.sourceApp ?? "Shared")), [summary?.assets]);
  const availabilityOptions = useMemo(() => uniqueOptions((summary?.assets ?? []).map((asset) => asset.availability?.primary ?? "unknown")), [summary?.assets]);
  const assets = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (summary?.assets ?? [])
      .filter((asset) => {
        const matchesLibrary = libraryId === "all" || asset.libraryId === libraryId;
        const matchesCrate = crateId === "all" || selectedCrateAssetIds.has(asset.id ?? asset.assetId ?? "");
        const matchesSource = sourceApp === "all" || (asset.sourceApp ?? "Shared") === sourceApp;
        const matchesAvailability = availability === "all" || (asset.availability?.primary ?? "unknown") === availability;
        const matchesQuery = !normalizedQuery || searchableAssetText(asset).includes(normalizedQuery);
        return matchesLibrary && matchesCrate && matchesSource && matchesAvailability && matchesQuery;
      })
      .sort((a, b) => compareAssets(a, b, sortBy));
  }, [summary?.assets, query, libraryId, crateId, selectedCrateAssetIds, sourceApp, availability, sortBy]);
  const statCards: Array<[string, number, LucideIcon]> = [
    ["Devices", summary?.counts?.devices ?? devices.length, MonitorUp],
    ["Libraries", summary?.counts?.libraries ?? libraries.length, Folder],
    ["Assets", summary?.counts?.assets ?? assets.length, Database],
    ["Groups", summary?.counts?.crates ?? crates.length, Folder],
    ["Locations", summary?.counts?.assetLocations ?? 0, HardDrive],
  ];
  const browserReadOnly = summary?.browserMode?.readOnly !== false;
  const selectedAsset = useMemo(
    () => (summary?.assets ?? []).find((asset) => (asset.id ?? asset.assetId ?? asset.title) === selectedAssetId) ?? null,
    [selectedAssetId, summary?.assets],
  );
  const readiness = browserReadiness({
    config,
    summary,
    source,
    deviceId,
    deviceToken,
    localBrowserAccessUrl,
    localBrowserAccessToken,
  });
  const clearBrowserCredential = () => {
    window.localStorage.removeItem("vjtools.browserLibrary.deviceId");
    window.localStorage.removeItem("vjtools.browserLibrary.deviceToken");
    window.localStorage.removeItem("vjtools.browserLibrary.localBrowserAccessUrl");
    window.localStorage.removeItem("vjtools.browserLibrary.localBrowserAccessToken");
    setDeviceId("");
    setDeviceToken("");
    setLocalBrowserAccessUrl("");
    setLocalBrowserAccessToken("");
    setCredentialText("");
    setSource(null);
    setSummary(null);
  };
  const importBrowserCredential = () => {
    const text = credentialText.trim();
    if (!text) {
      setError("Paste the browser credential JSON copied from a trusted desktop install.");
      return;
    }
    try {
      applyBrowserCredential(parseBrowserCredentialJson(text));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read credential JSON.");
    }
  };
  const requestAssetAccess = async (asset: NonNullable<BrowserSummary["assets"]>[number]) => {
    const assetId = asset.id ?? asset.assetId;
    const trimmedDeviceId = deviceId.trim();
    const trimmedDeviceToken = deviceToken.trim();
    if (!assetId || !trimmedDeviceId || !trimmedDeviceToken) {
      setError("Pair a trusted device credential before requesting browser access.");
      return;
    }

    setRequestingAssetId(assetId);
    setActionMessage(null);
    setError(null);
    try {
      const response = await fetch(coordinatorApiUrl('/api/client/sync-jobs/create'), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          deviceId: trimmedDeviceId,
          deviceToken: trimmedDeviceToken,
          targetDeviceId: asset.browserAccess?.deviceId ?? trimmedDeviceId,
          assetId,
          mode: "cache_on_demand",
          priority: 25,
          metadata: {
            requestSource: "browser_library",
            requestKind: "asset_access",
            assetTitle: asset.title,
            assetMediaType: asset.mediaType,
            assetAvailability: asset.availability?.primary ?? "unknown",
            browserAccessMode: asset.browserAccess?.mode ?? "metadata_only",
            targetDeviceName: asset.browserAccess?.deviceName,
            requestedAt: new Date().toISOString(),
          },
        }),
      });
      const json = (await response.json().catch(() => null)) as JobResponse | null;
      if (!response.ok || !json?.ok) throw new Error(json?.error ?? `Coordinator returned HTTP ${response.status}`);
      setActionMessage(`Queued browser access request for "${asset.title}" (${json.data?.syncJobId ?? "sync job"}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not queue browser access request.");
    } finally {
      setRequestingAssetId(null);
    }
  };

  const openSharedCoordinatorAccount = () => {
    window.open(sharedCoordinatorPageUrl("/account"), "_blank", "noopener,noreferrer");
  };

  const openSharedCoordinatorPairing = () => {
    window.open(sharedCoordinatorPageUrl("/pair"), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#090b0f] text-[#edf2f7]">
      <header className="border-b border-white/10 bg-[#0d1117]">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#62d3b5]">cloud.vj.tools</p>
            <h1 className="mt-1 text-2xl font-semibold">Browser Library</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-[#9fb0c3]">
              Browse synced Event.Tools and VJ.Tools metadata from the web. Original media stays on the user&apos;s machines; availability shows where files can be reached.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              className="flex h-10 items-center justify-center gap-2 rounded-md border border-[#263342] px-3 text-sm text-[#d9e2ec] disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={openSharedCoordinatorAccount}
              className="flex h-10 items-center justify-center gap-2 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm text-[#d9e2ec]"
            >
              <KeyRound className="h-4 w-4" />
              Shared Account
            </button>
            <button
              type="button"
              onClick={openSharedCoordinatorPairing}
              className="flex h-10 items-center justify-center gap-2 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm text-[#d9e2ec]"
            >
              <ShieldCheck className="h-4 w-4" />
              Open Pairing
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-5 px-6 py-6 xl:grid-cols-[280px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <p className="text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">Mode</p>
            <p className="mt-2 text-sm font-semibold">{source === "cloud" ? "Cloud org" : "Setup preview"}</p>
            <p className="mt-2 text-xs leading-5 text-[#9fb0c3]">
              {source === "device"
                ? "Paired device credential"
                : source === "cloud"
                ? summary?.organization?.name ?? "Signed-in organization"
                : config
                  ? `${config.clientApiBackend ?? "validated"} coordinator online`
                  : "Waiting for coordinator setup."}
            </p>
          </div>

          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#62d3b5]" />
              <h2 className="text-sm font-semibold">Browse As Paired Device</h2>
            </div>
            <p className="mt-2 text-xs leading-5 text-[#9fb0c3]">
              Paste a device credential from a trusted local install to browse metadata and
              availability without uploading original media.
            </p>
            <label className="mt-3 block">
              <span className="mb-1 flex items-center gap-1 text-xs text-[#7f8fa3]">
                <KeyRound className="h-3.5 w-3.5" />
                Credential JSON
              </span>
              <textarea
                value={credentialText}
                onChange={(event) => setCredentialText(event.target.value)}
                className="h-24 w-full resize-none rounded-md border border-[#263342] bg-[#0d1117] px-2 py-2 font-mono text-xs outline-none focus:border-[#62d3b5]"
                placeholder='{"deviceId":"...","deviceToken":"..."}'
                spellCheck={false}
              />
            </label>
            <button
              type="button"
              onClick={importBrowserCredential}
              disabled={!credentialText.trim()}
              className="mt-2 h-9 w-full rounded-md border border-[#263342] px-3 text-xs text-[#d9e2ec] disabled:opacity-50"
            >
              Import Credential
            </button>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-[#7f8fa3]">Device ID</span>
              <input
                value={deviceId}
                onChange={(event) => setDeviceId(event.target.value)}
                className="h-9 w-full rounded-md border border-[#263342] bg-[#0d1117] px-2 font-mono text-xs outline-none focus:border-[#62d3b5]"
                placeholder="cloud-device-id"
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-[#7f8fa3]">Device Token</span>
              <input
                value={deviceToken}
                onChange={(event) => setDeviceToken(event.target.value)}
                type="password"
                className="h-9 w-full rounded-md border border-[#263342] bg-[#0d1117] px-2 font-mono text-xs outline-none focus:border-[#62d3b5]"
                placeholder="stored locally in this browser"
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-[#7f8fa3]">Local Browser Access URL</span>
              <input
                value={localBrowserAccessUrl}
                onChange={(event) => setLocalBrowserAccessUrl(event.target.value)}
                className="h-9 w-full rounded-md border border-[#263342] bg-[#0d1117] px-2 font-mono text-xs outline-none focus:border-[#62d3b5]"
                placeholder="http://127.0.0.1:17645/api/v1/browser-library"
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-[#7f8fa3]">Local Browser Access Token</span>
              <input
                value={localBrowserAccessToken}
                onChange={(event) => setLocalBrowserAccessToken(event.target.value)}
                type="password"
                className="h-9 w-full rounded-md border border-[#263342] bg-[#0d1117] px-2 font-mono text-xs outline-none focus:border-[#62d3b5]"
                placeholder="included by desktop handoff"
              />
            </label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => void refresh()}
                disabled={loading || !deviceId.trim() || !deviceToken.trim()}
                className="h-9 rounded-md bg-[#62d3b5] px-3 text-xs font-semibold text-[#06110e] disabled:opacity-50"
              >
                Browse
              </button>
              <button
                type="button"
                onClick={clearBrowserCredential}
                className="h-9 rounded-md border border-[#263342] px-3 text-xs text-[#d9e2ec]"
              >
                Forget
              </button>
            </div>
          </div>

          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <div className="flex items-center gap-2">
              <MonitorUp className="h-4 w-4 text-[#62d3b5]" />
              <h2 className="text-sm font-semibold">Online Devices</h2>
            </div>
            <div className="mt-3 space-y-2">
              {devices.slice(0, 8).map((device) => (
                <div key={device.id ?? device.deviceId ?? device.name} className="rounded-md bg-[#0d1117] px-3 py-2">
                  <p className="truncate text-sm">{device.name}</p>
                  <p className="mt-1 font-mono text-xs text-[#9fb0c3]">{device.clientType} / {device.appProduct}</p>
                  {device.browserAccess ? (
                    <p className="mt-1 font-mono text-xs text-[#7f8fa3]">{formatBrowserAccess(device.browserAccess)}</p>
                  ) : null}
                </div>
              ))}
              {devices.length === 0 ? <p className="text-sm text-[#7f8fa3]">No devices have reported yet.</p> : null}
            </div>
          </div>

          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-[#62d3b5]" />
              <h2 className="text-sm font-semibold">Availability</h2>
            </div>
            <div className="mt-3 space-y-2">
              {Object.entries(summary?.availability ?? {}).map(([label, count]) => (
                <div key={label} className="flex items-center justify-between rounded-md bg-[#0d1117] px-3 py-2">
                  <span className="font-mono text-xs text-[#c8d6e5]">{label}</span>
                  <span className="font-mono text-sm">{count}</span>
                </div>
              ))}
              {Object.keys(summary?.availability ?? {}).length === 0 ? <p className="text-sm text-[#7f8fa3]">No locations reported yet.</p> : null}
            </div>
          </div>
        </aside>

        <section className="space-y-5">
          <div className="grid gap-3 md:grid-cols-5">
            {statCards.map(([label, value, Icon]) => {
              return (
                <div key={String(label)} className="rounded-md border border-white/10 bg-[#111821] p-4">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#62d3b5]" />
                    <p className="text-sm text-[#9fb0c3]">{label}</p>
                  </div>
                  <p className="mt-3 font-mono text-3xl font-semibold">{String(value)}</p>
                </div>
              );
            })}
          </div>

          {crates.length > 0 ? (
            <div className="rounded-md border border-white/10 bg-[#111821] p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">Loadouts & Collections</p>
                  <p className="mt-1 text-xs leading-5 text-[#9fb0c3]">
                    Synced VJ and Event.Tools groups are browsable as metadata-only collections. Selecting one filters the loaded asset rows; preview/action buttons still require an online paired desktop.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCrateId("all")}
                  className="h-8 rounded-md border border-[#263342] px-3 text-xs text-[#d9e2ec]"
                >
                  All Groups
                </button>
              </div>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {crates.map((crate) => {
                  const id = crateIdentity(crate);
                  const selected = crateId === id;
                  const loadedMemberCount = (summary?.assets ?? []).filter((asset) => (crate.assetIds ?? []).includes(asset.id ?? asset.assetId ?? "")).length;
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => setCrateId(selected ? "all" : id)}
                      className={`min-h-24 rounded-md border px-3 py-3 text-left ${selected ? "border-[#62d3b5] bg-[#14211f]" : "border-[#263342] bg-[#0d1117]"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">{crate.name}</p>
                          <p className="mt-1 font-mono text-xs text-[#9fb0c3]">{formatCrateSource(crate)}</p>
                        </div>
                        <span className="rounded border border-[#263342] px-1.5 py-0.5 font-mono text-[10px] text-[#c8d6e5]">
                          {crate.status ?? "draft"}
                        </span>
                      </div>
                      <p className="mt-3 text-xs leading-5 text-[#9fb0c3]">
                        {crate.assetCount ?? crate.assetIds?.length ?? 0} synced member{(crate.assetCount ?? crate.assetIds?.length ?? 0) === 1 ? "" : "s"}
                        {loadedMemberCount ? `, ${loadedMemberCount} loaded on this page` : ""}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md border border-[#5d2d2d] bg-[#211111] px-4 py-3 text-sm text-[#ffb4b4]">
              {error}
            </div>
          ) : null}

          {actionMessage ? (
            <div className="rounded-md border border-[#1f5d4f] bg-[#10211d] px-4 py-3 text-sm text-[#a7f3dd]">
              {actionMessage}
            </div>
          ) : null}

          {summary ? (
            <div className={`rounded-md border px-4 py-3 text-sm ${
              browserReadOnly
                ? "border-[#564826] bg-[#211c10] text-[#f6d99c]"
                : "border-[#1f5d4f] bg-[#10211d] text-[#a7f3dd]"
            }`}>
              <p className="font-semibold">{browserReadOnly ? "Read-only snapshot" : "Online coordination available"}</p>
              <p className="mt-1 leading-6">
                {summary.browserMode?.reason ?? (browserReadOnly
                  ? "Metadata and cached thumbnails are browsable, but preview/actions wait until a paired desktop reconnects."
                  : "A paired desktop is reachable, so preview/action requests can be coordinated without storing original media here.")}
              </p>
            </div>
          ) : null}

          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Cloud Browsing Readiness</p>
                <p className="mt-1 text-xs leading-5 text-[#9fb0c3]">
                  Browser access stays metadata-only until a paired desktop has synced catalog rows and has a fresh reachable heartbeat.
                </p>
              </div>
              <span className={`rounded border px-2 py-1 font-mono text-xs ${
                readiness.every((item) => item.ready)
                  ? "border-[#1f5d4f] text-[#62d3b5]"
                  : "border-[#564826] text-[#f6d99c]"
              }`}>
                {readiness.every((item) => item.ready) ? "ready" : "setup needed"}
              </span>
            </div>
            <div className="grid gap-2 md:grid-cols-4">
              {readiness.map((item) => (
                <div key={item.label} className="rounded-md border border-[#263342] bg-[#0d1117] px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold">{item.label}</p>
                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] ${
                      item.ready ? "border-[#1f5d4f] text-[#62d3b5]" : "border-[#564826] text-[#f6d99c]"
                    }`}>
                      {item.ready ? "ready" : "pending"}
                    </span>
                  </div>
                  <p className="mt-1 min-h-8 text-xs leading-4 text-[#9fb0c3]">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {!summary && config ? (
            <div className="rounded-md border border-[#263342] bg-[#111821] p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 text-[#62d3b5]" />
                <div>
                  <h2 className="text-base font-semibold">Coordinator Is Online, Org Data Is Not Wired Yet</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-[#9fb0c3]">
                    Desktop clients can discover the hosted coordinator and stay local-first. Browser library rows appear here after production Clerk, Convex, and a signed-in organization are configured, then VJ.Tools or Event.Tools reports metadata from a paired device.
                  </p>
                  <div className="mt-4 grid gap-3 text-sm md:grid-cols-3">
                    <SetupFlag label="Local-only allowed" value={String(config.pairing?.localOnlyAllowed ?? true)} />
                    <SetupFlag label="Original uploads required" value={String(config.originalMediaUploadRequired)} />
                    <SetupFlag label="Protocol" value={config.protocolVersion} />
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-md border border-white/10 bg-[#111821] p-4">
            <div className="grid gap-3 lg:grid-cols-[1fr_160px_160px_170px_180px_180px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7f8fa3]" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search asset metadata"
                  className="h-10 w-full rounded-md border border-[#263342] bg-[#0d1117] pl-9 pr-3 text-sm outline-none focus:border-[#62d3b5]"
                />
              </label>
              <select
                value={sourceApp}
                onChange={(event) => setSourceApp(event.target.value)}
                className="h-10 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm outline-none focus:border-[#62d3b5]"
              >
                <option value="all">All sources</option>
                {sourceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
                className="h-10 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm outline-none focus:border-[#62d3b5]"
              >
                <option value="title">Sort by name</option>
                <option value="source">Sort by source</option>
                <option value="availability">Sort by availability</option>
                <option value="type">Sort by type</option>
                <option value="locations">Sort by locations</option>
              </select>
              <select
                value={availability}
                onChange={(event) => setAvailability(event.target.value)}
                className="h-10 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm outline-none focus:border-[#62d3b5]"
              >
                <option value="all">All availability</option>
                {availabilityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                value={crateId}
                onChange={(event) => setCrateId(event.target.value)}
                className="h-10 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm outline-none focus:border-[#62d3b5]"
              >
                <option value="all">All groups</option>
                {crates.map((crate) => {
                  const id = crateIdentity(crate);
                  return (
                    <option key={id} value={id}>
                      {crate.name}
                    </option>
                  );
                })}
              </select>
              <select
                value={libraryId}
                onChange={(event) => setLibraryId(event.target.value)}
                className="h-10 rounded-md border border-[#263342] bg-[#0d1117] px-3 text-sm outline-none focus:border-[#62d3b5]"
              >
                <option value="all">All libraries</option>
                {libraries.map((library) => {
                  const id = library.id ?? library.libraryId ?? library.name;
                  return (
                    <option key={id} value={id}>
                      {library.name}
                    </option>
                  );
                })}
              </select>
            </div>
            <p className="mt-3 text-xs text-[#7f8fa3]">
              Showing {assets.length} filtered records from {(summary?.assets ?? []).length} loaded of {summary?.assetPage?.total ?? summary?.counts?.assets ?? 0} synced metadata records.
              {selectedCrate ? ` Group "${selectedCrate.name}" has ${selectedCrate.assetCount ?? selectedCrate.assetIds?.length ?? 0} synced member${(selectedCrate.assetCount ?? selectedCrate.assetIds?.length ?? 0) === 1 ? "" : "s"}.` : ""}
            </p>
          </div>

          <div className="rounded-md border border-white/10 bg-[#111821]">
            <div className="hidden grid-cols-[86px_minmax(220px,1.45fr)_110px_120px_130px_120px_150px_150px] gap-3 border-b border-white/10 px-4 py-3 text-xs uppercase tracking-[0.12em] text-[#7f8fa3] md:grid">
              <span>Thumb</span>
              <span>Asset</span>
              <span>Type</span>
              <span>Availability</span>
              <span>Shape</span>
              <span>Locations</span>
              <span>Browser Access</span>
              <span>Action</span>
            </div>
            <div className="divide-y divide-white/10">
              {assets.map((asset) => {
                const assetId = asset.id ?? asset.assetId;
                const stableAssetId = assetId ?? asset.title;
                const canRequestAccess = Boolean(!browserReadOnly && deviceId.trim() && deviceToken.trim() && asset.browserAccess?.deviceId && asset.browserAccess?.reachable);
                return (
                  <div key={stableAssetId} className={`grid gap-3 px-4 py-3 text-sm md:grid-cols-[86px_minmax(220px,1.45fr)_110px_120px_130px_120px_150px_150px] ${selectedAssetId === stableAssetId ? "bg-[#14211f]" : ""}`}>
                    <AssetThumbnail asset={asset} />
                    <div>
                      <p className="truncate font-medium">{asset.title}</p>
                      <p className="mt-1 truncate font-mono text-xs text-[#7f8fa3]">{asset.sourceApp ?? "Shared"} / {asset.libraryId ?? "unassigned library"}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {assetTags(asset).slice(0, 6).map((tag) => (
                          <span key={tag} className="rounded border border-[#263342] px-1.5 py-0.5 text-[11px] text-[#9fb0c3]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className="font-mono text-xs text-[#c8d6e5]">{asset.mediaType}</span>
                    <span className="font-mono text-xs text-[#c8d6e5]">{formatAvailability(asset.availability)}</span>
                    <span className="font-mono text-xs text-[#c8d6e5]">{formatAssetShape(asset)}</span>
                    <span className="font-mono text-xs text-[#c8d6e5]">{asset.locationCount ?? 0} loc / {asset.fileCount ?? 0} files</span>
                    <span className="font-mono text-xs text-[#c8d6e5]" title={asset.browserAccess?.note}>{formatBrowserAccess(asset.browserAccess)}</span>
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedAssetId(stableAssetId)}
                        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#263342] px-2 text-xs text-[#d9e2ec]"
                        title="Inspect synced metadata, availability, and browser access state without opening or uploading media."
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Details
                      </button>
                      <button
                        type="button"
                        onClick={() => void requestAssetAccess(asset)}
                        disabled={!canRequestAccess || requestingAssetId === assetId}
                        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#263342] px-2 text-xs text-[#d9e2ec] disabled:cursor-not-allowed disabled:opacity-45"
                        title={canRequestAccess ? "Queue a metadata-only cache/open intent for the paired online device that has this asset. The website still does not host the media." : "Metadata is browsable, but preview/actions wait until the paired desktop app is online and reachable."}
                      >
                        <Send className="h-3.5 w-3.5" />
                        {requestingAssetId === assetId ? "Requesting" : "Request"}
                      </button>
                    </div>
                  </div>
                );
              })}
              {assets.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-[#7f8fa3]">
                  {summary?.assets?.length
                    ? "No synced asset metadata matches the current filters."
                    : "No synced asset metadata yet. Pair a local desktop install and report the library from app Settings."}
                </div>
              ) : null}
            </div>
            {summary?.assetPage?.hasMore ? (
              <div className="border-t border-white/10 px-4 py-4 text-center">
                <button
                  type="button"
                  onClick={() => void loadMoreAssets()}
                  disabled={loadingMore}
                  className="h-10 rounded-md border border-[#263342] px-4 text-sm text-[#d9e2ec] disabled:opacity-60"
                >
                  {loadingMore ? "Loading metadata..." : `Load more metadata (${(summary.assetPage.total ?? 0) - (summary.assets?.length ?? 0)} remaining)`}
                </button>
              </div>
            ) : null}
          </div>
          {selectedAsset ? (
            <AssetDetailPanel
              asset={selectedAsset}
              browserReadOnly={browserReadOnly}
              localBrowserAccessUrl={localBrowserAccessUrl}
              localBrowserAccessToken={localBrowserAccessToken}
              onClose={() => setSelectedAssetId(null)}
              onRequestAccess={() => void requestAssetAccess(selectedAsset)}
              requestDisabled={Boolean(browserReadOnly || !deviceId.trim() || !deviceToken.trim() || !selectedAsset.browserAccess?.deviceId || !selectedAsset.browserAccess?.reachable || requestingAssetId === (selectedAsset.id ?? selectedAsset.assetId))}
              requesting={requestingAssetId === (selectedAsset.id ?? selectedAsset.assetId)}
            />
          ) : null}
        </section>
      </main>
    </div>
  );
}

function summaryPageUrl(path: string, offset: number) {
  const params = new URLSearchParams({
    assetLimit: String(BROWSER_ASSET_PAGE_SIZE),
    assetOffset: String(Math.max(0, offset)),
    offset: String(Math.max(0, offset)),
  });
  return `${coordinatorApiUrl(path)}?${params.toString()}`;
}

function coordinatorApiUrl(path: string) {
  return sharedCoordinatorPageUrl(path);
}

function sharedCoordinatorPageUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${SHARED_COORDINATOR_URL}${normalizedPath}`;
}

function AssetDetailPanel({
  asset,
  browserReadOnly,
  localBrowserAccessUrl,
  localBrowserAccessToken,
  requesting,
  requestDisabled,
  onClose,
  onRequestAccess,
}: {
  asset: NonNullable<BrowserSummary["assets"]>[number];
  browserReadOnly: boolean;
  localBrowserAccessUrl: string;
  localBrowserAccessToken: string;
  requesting: boolean;
  requestDisabled: boolean;
  onClose: () => void;
  onRequestAccess: () => void;
}) {
  const tags = assetTags(asset);
  const metadataRows = assetMetadataRows(asset);
  const availabilityRows = Object.entries(asset.availability?.counts ?? {});
  const syncRows = Object.entries(asset.availability?.syncStatuses ?? {});
  const localPreviewUrl = localDerivativeUrl(localBrowserAccessUrl, localBrowserAccessToken, asset.metadata?.localFileId, "preview");
  const localThumbnailUrl = localDerivativeUrl(localBrowserAccessUrl, localBrowserAccessToken, asset.metadata?.localFileId, "thumbnail");
  const cachedPosterDataUrl = safePosterDataUrl(asset.derivatives?.thumbnail?.browserPosterDataUrl);
  const canAttemptLocalPreview = Boolean(!browserReadOnly && asset.browserAccess?.reachable && localBrowserAccessUrl && localBrowserAccessToken && asset.metadata?.localFileId);
  const healthUrl = localDesktopHealthUrl(localBrowserAccessUrl, localBrowserAccessToken);
  const [localDesktopProbe, setLocalDesktopProbe] = useState<{
    url: string;
    status: "online" | "offline";
    message: string;
  } | null>(null);
  const localDesktopStatus: "idle" | "checking" | "online" | "offline" = !canAttemptLocalPreview || !healthUrl
    ? "idle"
    : localDesktopProbe?.url === healthUrl
      ? localDesktopProbe.status
      : "checking";
  const localDesktopMessage = !canAttemptLocalPreview || !healthUrl
    ? null
    : localDesktopProbe?.url === healthUrl
      ? localDesktopProbe.message
      : "Checking the paired desktop preview endpoint...";
  const canShowLocalPreview = canAttemptLocalPreview && localDesktopStatus === "online";

  useEffect(() => {
    let alive = true;
    if (!canAttemptLocalPreview || !healthUrl) {
      return () => {
        alive = false;
      };
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2500);
    void fetch(healthUrl, { cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json().catch(() => null)) as { ok?: boolean; originalMediaServed?: boolean } | null;
        if (!alive) return;
        if (response.ok && body?.ok === true && body.originalMediaServed === false) {
          setLocalDesktopProbe({
            url: healthUrl,
            status: "online",
            message: "Paired desktop preview endpoint is online. Preview uses derivatives only.",
          });
        } else {
          setLocalDesktopProbe({
            url: healthUrl,
            status: "offline",
            message: "The paired desktop endpoint did not confirm derivative-only browser access.",
          });
        }
      })
      .catch(() => {
        if (!alive) return;
        setLocalDesktopProbe({
          url: healthUrl,
          status: "offline",
          message: "This browser cannot reach the paired desktop preview endpoint yet.",
        });
      })
      .finally(() => window.clearTimeout(timer));

    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [canAttemptLocalPreview, healthUrl]);

  return (
    <div className="rounded-md border border-white/10 bg-[#111821] p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-4">
          <AssetThumbnail asset={asset} />
          <div>
            <p className="text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">Metadata detail</p>
            <h2 className="mt-1 text-lg font-semibold">{asset.title}</h2>
            <p className="mt-1 font-mono text-xs text-[#9fb0c3]">
              {(asset.sourceApp ?? "Shared")} / {asset.mediaType} / {asset.libraryId ?? "unassigned library"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRequestAccess}
            disabled={requestDisabled}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-[#263342] px-3 text-xs text-[#d9e2ec] disabled:cursor-not-allowed disabled:opacity-45"
            title={requestDisabled ? "Preview/actions wait until a paired desktop app is online and reachable." : "Queue a request for the online desktop that owns this asset."}
          >
            <Send className="h-3.5 w-3.5" />
            {requesting ? "Requesting" : "Request access"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#263342] text-[#d9e2ec]"
            aria-label="Close asset detail"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-md border px-3 py-2 text-sm ${
        browserReadOnly || !asset.browserAccess?.reachable
          ? "border-[#564826] bg-[#211c10] text-[#f6d99c]"
          : "border-[#1f5d4f] bg-[#10211d] text-[#a7f3dd]"
      }`}>
        {browserReadOnly || !asset.browserAccess?.reachable
          ? "Cached metadata and poster thumbnails are available. Preview/open actions stay disabled until the paired desktop reconnects."
          : "A paired desktop is reachable for this asset. Browser actions are coordinated through that device; original media is still not stored here."}
      </div>

      <div className="mt-4 rounded-md border border-[#263342] bg-[#0d1117] p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">Online Desktop Preview</p>
          <span className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
            localDesktopStatus === "online"
              ? "border-[#1f5d4f] text-[#62d3b5]"
              : localDesktopStatus === "checking"
                ? "border-[#564826] text-[#f6d99c]"
                : "border-[#263342] text-[#9fb0c3]"
          }`}>
            {localDesktopStatus}
          </span>
        </div>
        {canShowLocalPreview && localPreviewUrl ? (
          <video
            src={localPreviewUrl}
            poster={localThumbnailUrl ?? undefined}
            controls
            muted
            playsInline
            className="aspect-video w-full rounded-md bg-black object-contain"
          />
        ) : canShowLocalPreview && localThumbnailUrl ? (
          <div
            aria-label="Thumbnail from the paired desktop"
            className="aspect-video w-full rounded-md bg-black bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url("${localThumbnailUrl}")` }}
          />
        ) : cachedPosterDataUrl ? (
          <div
            aria-label="Cached thumbnail poster from synced metadata"
            className="aspect-video w-full rounded-md bg-black bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url("${cachedPosterDataUrl}")` }}
          />
        ) : (
          <p className="text-sm leading-6 text-[#9fb0c3]">
            Preview stays disabled until this browser has a local access URL from a paired desktop
            handoff, the local access token is present, and that desktop is online. The cloud site
            still does not store original media.
          </p>
        )}
        {localDesktopMessage ? (
          <p className="mt-2 text-xs leading-5 text-[#9fb0c3]">{localDesktopMessage}</p>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <DetailSection title="Availability">
          <DetailRow label="Primary" value={asset.availability?.primary ?? "unknown"} />
          <DetailRow label="Browser access" value={formatBrowserAccess(asset.browserAccess)} />
          <DetailRow label="Locations" value={`${asset.locationCount ?? 0}`} />
          <DetailRow label="Files" value={`${asset.fileCount ?? 0}`} />
          {availabilityRows.map(([label, count]) => <DetailRow key={label} label={label} value={`${count}`} />)}
          {syncRows.map(([label, count]) => <DetailRow key={label} label={`sync:${label}`} value={`${count}`} />)}
        </DetailSection>

        <DetailSection title="Media Metadata">
          <DetailRow label="Shape" value={formatAssetShape(asset)} />
          <DetailRow label="Container" value={asset.container ?? asset.metadata?.extension ?? "unknown"} />
          <DetailRow label="Codec" value={asset.codec ?? "unknown"} />
          <DetailRow label="Size" value={formatBytes(asset.metadata?.sizeBytes)} />
          {metadataRows.map(([label, value]) => <DetailRow key={label} label={label} value={value} />)}
        </DetailSection>

        <DetailSection title="Derivatives">
          <DetailRow label="Thumbnail" value={asset.derivatives?.hasThumbnail || asset.metadata?.hasThumbnail ? "reported" : "none"} />
          <DetailRow label="Proxy" value={asset.derivatives?.hasProxy || asset.metadata?.hasProxy ? "reported" : "none"} />
          <DetailRow label="Poster" value={safePosterDataUrl(asset.derivatives?.thumbnail?.browserPosterDataUrl) ? "cached for offline browsing" : "not cached"} />
          <DetailRow label="Thumbnail file" value={asset.derivatives?.thumbnail?.fileName ?? "not reported"} />
          <DetailRow label="Proxy file" value={asset.derivatives?.proxy?.fileName ?? "not reported"} />
        </DetailSection>
      </div>

      <div className="mt-4">
        <p className="mb-2 text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">Tags</p>
        <div className="flex flex-wrap gap-1.5">
          {tags.length > 0 ? tags.map((tag) => (
            <span key={tag} className="rounded border border-[#263342] px-2 py-1 text-xs text-[#9fb0c3]">{tag}</span>
          )) : <span className="text-sm text-[#7f8fa3]">No synced tags.</span>}
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-[#263342] bg-[#0d1117] p-3">
      <p className="mb-2 text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="text-[#7f8fa3]">{label}</span>
      <span className="max-w-[65%] break-words text-right font-mono text-[#d9e2ec]">{value}</span>
    </div>
  );
}

function assetMetadataRows(asset: NonNullable<BrowserSummary["assets"]>[number]): Array<[string, string]> {
  const metadata = asset.metadata ?? {};
  return [
    ["local file", metadata.localFileId ? metadata.localFileId : undefined],
    ["scope", metadata.scope],
    ["host", metadata.hostKind],
    ["drive", metadata.driveLabel],
    ["variants", numberDetail(metadata.variantCount)],
    ["mp4 variants", numberDetail(metadata.mp4VariantCount)],
    ["dxv variants", numberDetail(metadata.dxvVariantCount)],
    ["beats", numberDetail(metadata.beatCount)],
    ["loop hamming", numberDetail(metadata.loopHamming)],
    ["creators", listDetail(metadata.creatorNames)],
    ["projects", listDetail(metadata.projectNames)],
    ["sets", listDetail(metadata.setNames)],
    ["artists", listDetail(metadata.artistNames)],
    ["Resolume decks", listDetail(metadata.resolumeDeckNames)],
    ["Resolume color", metadata.resolumeClipColor],
    ["modified", dateDetail(metadata.modifiedAt)],
    ["last verified", dateDetail(metadata.lastVerifiedAt)],
    ["layers", numberDetail(metadata.layerCount)],
    ["attached assets", numberDetail(metadata.attachedAssetCount)],
    ["generations", numberDetail(metadata.generationRunCount)],
    ["exports", numberDetail(metadata.exportCount)],
  ].filter((row): row is [string, string] => Boolean(row[1]));
}

function browserReadiness(input: {
  config: CoordinatorConfig | null;
  summary: BrowserSummary | null;
  source: "device" | "cloud" | "local_mock" | null;
  deviceId: string;
  deviceToken: string;
  localBrowserAccessUrl: string;
  localBrowserAccessToken: string;
}) {
  const coordinatorReady = Boolean(input.config && input.config.cloudRequired === false && input.config.originalMediaUploadRequired === false);
  const credentialReady = Boolean(input.deviceId.trim() && input.deviceToken.trim());
  const metadataCount = input.summary?.counts?.assets ?? input.summary?.assets?.length ?? 0;
  const metadataReady = metadataCount > 0;
  const onlineDesktopCount = input.summary?.browserMode?.reachableDeviceCount ?? 0;
  const previewHandoffReady = Boolean(input.localBrowserAccessUrl.trim() && input.localBrowserAccessToken.trim());
  const browserOnlineReady = onlineDesktopCount > 0 && previewHandoffReady;
  return [
    {
      label: "Coordinator",
      ready: coordinatorReady,
      detail: input.config
        ? `${input.config.clientApiBackend ?? "validated"} / local-only allowed`
        : "Load the hosted coordinator config.",
    },
    {
      label: "Paired Credential",
      ready: credentialReady || input.source === "cloud",
      detail: credentialReady
        ? "This browser has a trusted device credential."
        : input.source === "cloud"
          ? "Using signed-in organization access."
        : "Open your local desktop settings and copy/open Browser Library.",
    },
    {
      label: "Synced Metadata",
      ready: metadataReady,
      detail: metadataReady
        ? `${metadataCount} metadata record${metadataCount === 1 ? "" : "s"} available.`
        : "Pair a desktop and run metadata sync from app Settings.",
    },
    {
      label: "Online Desktop",
      ready: browserOnlineReady,
      detail: onlineDesktopCount > 0
        ? previewHandoffReady
          ? `${onlineDesktopCount} reachable desktop${onlineDesktopCount === 1 ? "" : "s"} for preview requests.`
          : "A desktop is online, but this browser needs the local preview handoff token."
        : "Metadata remains read-only until a paired desktop reconnects.",
    },
  ];
}

function localDerivativeUrl(baseUrl: string, accessToken: string, localFileId: string | undefined, role: "thumbnail" | "preview") {
  const normalized = normalizeLocalBrowserAccessUrl(baseUrl);
  const token = normalizeLocalBrowserAccessToken(accessToken);
  const trimmedId = localFileId?.trim();
  if (!normalized || !token || !trimmedId) return null;
  return withAccessToken(`${normalized}/files/${encodeURIComponent(trimmedId)}/${role}`, token);
}

function localDesktopHealthUrl(baseUrl: string, accessToken: string) {
  const normalized = normalizeLocalBrowserAccessUrl(baseUrl);
  const token = normalizeLocalBrowserAccessToken(accessToken);
  return normalized && token ? withAccessToken(`${normalized}/health`, token) : "";
}

function uniqueOptions(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function crateIdentity(crate: NonNullable<BrowserSummary["crates"]>[number]) {
  return crate.id ?? crate.crateId ?? crate.localCrateId ?? crate.name;
}

function formatCrateSource(crate: NonNullable<BrowserSummary["crates"]>[number]) {
  if (crate.sourceKind === "vj_loadout") return "VJ loadout";
  if (crate.sourceKind === "vj_collection") return "VJ collection";
  if (crate.sourceKind === "event_tools_project") return "Event.Tools project";
  return crate.kind ? `${crate.kind} group` : "cloud group";
}

function compareAssets(
  a: NonNullable<BrowserSummary["assets"]>[number],
  b: NonNullable<BrowserSummary["assets"]>[number],
  sortBy: "title" | "source" | "availability" | "type" | "locations",
) {
  if (sortBy === "locations") {
    return (b.locationCount ?? 0) - (a.locationCount ?? 0) || a.title.localeCompare(b.title);
  }
  if (sortBy === "source") {
    return (a.sourceApp ?? "Shared").localeCompare(b.sourceApp ?? "Shared") || a.title.localeCompare(b.title);
  }
  if (sortBy === "availability") {
    return availabilityRank(a.availability?.primary).localeCompare(availabilityRank(b.availability?.primary)) || a.title.localeCompare(b.title);
  }
  if (sortBy === "type") {
    return a.mediaType.localeCompare(b.mediaType) || a.title.localeCompare(b.title);
  }
  return a.title.localeCompare(b.title);
}

function availabilityRank(value: string | undefined) {
  const order = ["original_available", "proxy_available", "thumbnail_available", "metadata_only", "stale", "missing", "unknown"];
  const index = order.indexOf(value ?? "unknown");
  return String(index < 0 ? order.length : index).padStart(2, "0");
}

function searchableAssetText(asset: NonNullable<BrowserSummary["assets"]>[number]) {
  return [
    asset.title,
    asset.mediaType,
    asset.sourceApp,
    asset.codec,
    asset.container,
    ...(asset.tags ?? []),
    ...assetTags(asset),
    asset.metadata?.extension,
    asset.metadata?.scope,
    asset.metadata?.hostKind,
    asset.metadata?.driveLabel,
    asset.metadata?.localEventId,
    asset.metadata?.resolumeClipColor,
    ...(asset.metadata?.creatorNames ?? []),
    ...(asset.metadata?.projectNames ?? []),
    ...(asset.metadata?.setNames ?? []),
    ...(asset.metadata?.artistNames ?? []),
    ...(asset.metadata?.resolumeDeckNames ?? []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function assetTags(asset: NonNullable<BrowserSummary["assets"]>[number]) {
  const derived = [
    asset.metadata?.extension,
    asset.metadata?.scope && asset.metadata.scope !== "library" ? `scope:${asset.metadata.scope}` : undefined,
    asset.metadata?.hasProxy ? "proxy" : undefined,
    asset.metadata?.hasThumbnail ? "thumbnail" : undefined,
    asset.metadata?.hasScrubStrip ? "scrub" : undefined,
    asset.metadata?.hasDxvVariant ? "dxv" : undefined,
    asset.metadata?.hasMp4Variant ? "mp4" : undefined,
    asset.metadata?.resolumeClipColor ? `color:${asset.metadata.resolumeClipColor}` : undefined,
    ...(asset.metadata?.creatorNames ?? []),
    ...(asset.metadata?.projectNames ?? []),
    ...(asset.metadata?.setNames ?? []),
    ...(asset.metadata?.artistNames ?? []),
    ...(asset.metadata?.resolumeDeckNames ?? []),
    asset.metadata?.layerCount ? `${asset.metadata.layerCount} layers` : undefined,
    asset.metadata?.attachedAssetCount ? `${asset.metadata.attachedAssetCount} assets` : undefined,
    asset.metadata?.generationRunCount ? `${asset.metadata.generationRunCount} generations` : undefined,
  ];
  return [...(asset.tags ?? []), ...derived]
    .filter((tag): tag is string => Boolean(tag))
    .filter((tag, index, tags) => tags.indexOf(tag) === index);
}

function AssetThumbnail({ asset }: { asset: NonNullable<BrowserSummary["assets"]>[number] }) {
  const hasThumbnail = asset.derivatives?.hasThumbnail || asset.metadata?.hasThumbnail;
  const hasProxy = asset.derivatives?.hasProxy || asset.metadata?.hasProxy;
  const posterDataUrl = safePosterDataUrl(asset.derivatives?.thumbnail?.browserPosterDataUrl);
  const reachable = asset.browserAccess?.reachable === true;
  const label = hasThumbnail ? "Thumb" : hasProxy ? "Proxy" : asset.mediaType.slice(0, 5);
  const detail = reachable ? "online" : posterDataUrl ? "cached" : hasThumbnail ? "record" : "meta";

  return (
    <div
      className="relative flex aspect-video w-[78px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#263342] bg-[#0d1117]"
      title={
        reachable
          ? "A paired online desktop can serve preview/open requests for this metadata record."
          : hasThumbnail
            ? "Thumbnail metadata was synced. Preview/open waits for the desktop app to reconnect."
            : "Metadata-only record. Preview/open waits for an online desktop app."
      }
    >
      {posterDataUrl ? (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${posterDataUrl}")` }} />
      ) : (
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(98,211,181,0.22),rgba(116,146,178,0.08))]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#090b0f]/75 via-transparent to-transparent" />
      <div className="relative text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#d9e2ec]">{label}</p>
        <p className={`mt-0.5 font-mono text-[10px] ${reachable ? "text-[#62d3b5]" : "text-[#9fb0c3]"}`}>{detail}</p>
      </div>
      {hasProxy ? <span className="absolute bottom-1 right-1 rounded bg-[#090b0f]/80 px-1 font-mono text-[9px] text-[#62d3b5]">mp4</span> : null}
    </div>
  );
}

function safePosterDataUrl(value: string | undefined) {
  if (!value) return undefined;
  if (!/^data:image\/(?:jpeg|jpg|png|webp|gif);base64,[a-z0-9+/=]+$/i.test(value)) return undefined;
  if (value.length > 120_000) return undefined;
  return value;
}

function formatAvailability(availability: AssetAvailability | undefined) {
  if (!availability) return "unknown";
  const count = availability.counts[availability.primary] ?? 0;
  return count > 1 ? `${availability.primary} x${count}` : availability.primary;
}

function formatAssetShape(asset: NonNullable<BrowserSummary["assets"]>[number]) {
  const shape = asset.width && asset.height ? `${asset.width}x${asset.height}` : null;
  const duration = typeof asset.durationMs === "number" ? formatDuration(asset.durationMs) : null;
  const projectBits = [
    asset.metadata?.layerCount ? `${asset.metadata.layerCount} layers` : null,
    asset.metadata?.exportCount ? `${asset.metadata.exportCount} exports` : null,
  ].filter(Boolean).join(" / ");
  return [shape, duration, asset.codec, projectBits || null].filter(Boolean).join(" / ") || "metadata";
}

function formatBrowserAccess(access: BrowserAccess | undefined) {
  if (!access) return "metadata only";
  if (access.reachable) {
    const via = access.mode === "local_api" ? "local API" : access.mode;
    const freshness = access.freshnessWindowMs ? ` / fresh ${Math.round(access.freshnessWindowMs / 1000)}s` : "";
    return access.deviceName ? `online via ${access.deviceName} / ${via}${freshness}` : `online / ${via}${freshness}`;
  }
  if (access.mode === "direct_pending") return "online handoff pending";
  if (access.mode === "metadata_only") return "metadata only";
  return `${access.mode} offline`;
}

function formatDuration(durationMs: number) {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes}:${String(rest).padStart(2, "0")}` : `${rest}s`;
}

function formatBytes(value: number | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return "unknown";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  return `${amount >= 10 || unit === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unit]}`;
}

function numberDetail(value: number | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : undefined;
}

function listDetail(value: string[] | undefined) {
  return value?.filter(Boolean).slice(0, 8).join(", ") || undefined;
}

function dateDetail(value: string | number | undefined) {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const numeric = typeof value === "number" ? value : Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 10_000_000_000 ? numeric : numeric * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function SetupFlag({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-[#0d1117] px-3 py-2">
      <p className="text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">{label}</p>
      <p className="mt-1 font-mono text-sm text-[#d9e2ec]">{value}</p>
    </div>
  );
}

function readStoredBrowserCredential(key: "deviceId" | "deviceToken" | "localBrowserAccessUrl" | "localBrowserAccessToken") {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(`vjtools.browserLibrary.${key}`) ?? "";
}

function deviceCredentialHeaders(deviceId: string, deviceToken: string): HeadersInit {
  return {
    authorization: `Bearer ${deviceToken}`,
    "x-vj-device-id": deviceId,
  };
}

function readHashBrowserCredential(): { deviceId: string; deviceToken: string; localBrowserAccessUrl?: string; localBrowserAccessToken?: string } | null {
  if (typeof window === "undefined" || !window.location.hash) return null;
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  const encodedCredential = params.get("credential");
  if (encodedCredential) return parseBrowserCredentialJson(base64UrlDecode(encodedCredential));
  const deviceId = params.get("deviceId")?.trim();
  const deviceToken = params.get("deviceToken")?.trim();
  const localBrowserAccessUrl = normalizeLocalBrowserAccessUrl(params.get("localBrowserAccessUrl") ?? undefined);
  const localBrowserAccessToken = normalizeLocalBrowserAccessToken(params.get("localBrowserAccessToken") ?? undefined);
  return deviceId && deviceToken ? { deviceId, deviceToken, localBrowserAccessUrl, localBrowserAccessToken } : null;
}

function parseBrowserCredentialJson(text: string): { deviceId: string; deviceToken: string; localBrowserAccessUrl?: string; localBrowserAccessToken?: string } {
  const parsed = JSON.parse(text) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Credential JSON must be an object with deviceId and deviceToken.");
  }

  const { deviceId, deviceToken } = parsed as { deviceId?: unknown; deviceToken?: unknown };
  if (typeof deviceId !== "string" || typeof deviceToken !== "string") {
    throw new Error("Credential JSON must include string deviceId and deviceToken fields.");
  }

  const trimmedDeviceId = deviceId.trim();
  const trimmedDeviceToken = deviceToken.trim();
  if (!trimmedDeviceId || !trimmedDeviceToken) {
    throw new Error("Credential JSON must include non-empty deviceId and deviceToken fields.");
  }

  const localBrowserAccessUrl = normalizeLocalBrowserAccessUrl((parsed as { localBrowserAccessUrl?: unknown }).localBrowserAccessUrl);
  const localBrowserAccessToken = normalizeLocalBrowserAccessToken((parsed as { localBrowserAccessToken?: unknown }).localBrowserAccessToken);
  return { deviceId: trimmedDeviceId, deviceToken: trimmedDeviceToken, localBrowserAccessUrl, localBrowserAccessToken };
}

function normalizeLocalBrowserAccessUrl(value: unknown) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    return url.toString().replace(/\/+$/, "");
  } catch {
    return "";
  }
}

function normalizeLocalBrowserAccessToken(value: unknown) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 256) return "";
  return /^[A-Za-z0-9._:-]+$/.test(trimmed) ? trimmed : "";
}

function withAccessToken(url: string, token: string) {
  const parsed = new URL(url);
  parsed.searchParams.set("accessToken", token);
  return parsed.toString();
}

function base64UrlDecode(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return atob(padded);
}
